import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { AsyncBoundary, Button } from '@kollez/design-system';
import { useOffers, type OfferListItem, type OffersListOpts } from '@/lib/queries';
import { formatLPA } from '@kollez/shared';

/**
 * /offers — Student's own offer list.
 *
 * Data model (post Pass 2):
 *   - GET /offers with page/limit/q/status/ctcMin/ctcMax/sortBy/sortDir — server
 *     returns {offers, summary.byStatus} + {page,limit,total}.
 *
 * Fabrications removed vs the old page:
 *   - `daysUntil` shown as a plain string with no urgency band → real red/amber/green chip
 *   - No filters/sort/search → segmented tabs + sort dropdown, all mapped to Pass 2 params
 *   - No KPI header → real summary rollup counts
 *   - No undo-window countdown → live "N min left to undo" chip if undoWindowEndsAt in future
 */

// Status buckets keyed off the Offer state machine (see models/Offer.ts).
// AWAITING = uploaded/ready/with_college/released/opened
// ACCEPTED = accepted/accepted_final
// CLOSED   = declined/undone/revoked/expired
type Seg = 'awaiting' | 'accepted' | 'closed';
const SEG_STATUSES: Record<Seg, string[]> = {
  awaiting: ['uploaded', 'ready', 'with_college', 'released', 'opened'],
  accepted: ['accepted', 'accepted_final'],
  closed: ['declined', 'undone', 'revoked', 'expired'],
};

type SortKey = NonNullable<OffersListOpts['sortBy']>;
const PAGE_SIZE = 25;

function initials(name?: string): string {
  return (name ?? '?').trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 3).toUpperCase();
}

function classifyDeadline(iso?: string): { days: number | null; tone: 'danger' | 'warning' | 'muted' | 'success'; label: string } {
  if (!iso) return { days: null, tone: 'muted', label: 'No deadline' };
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms)) return { days: null, tone: 'muted', label: 'No deadline' };
  const days = Math.round(ms / 86_400_000);
  if (days < 0) return { days, tone: 'muted', label: 'Deadline passed' };
  if (days <= 2) return { days, tone: 'danger', label: days === 0 ? 'Due today' : `${days}d left` };
  if (days <= 7) return { days, tone: 'warning', label: `${days}d left` };
  return { days, tone: 'success', label: `${days}d left` };
}

function statusChip(status: string): { label: string; tone: 'success' | 'warning' | 'muted' | 'info' } {
  const s = status.toLowerCase();
  if (s === 'accepted_final') return { label: 'Accepted', tone: 'success' };
  if (s === 'accepted') return { label: 'Accepted (undo open)', tone: 'warning' };
  if (s === 'released' || s === 'opened') return { label: 'Awaiting your reply', tone: 'info' };
  if (s === 'declined') return { label: 'Declined', tone: 'muted' };
  if (s === 'undone') return { label: 'Undone', tone: 'muted' };
  if (s === 'revoked') return { label: 'Revoked', tone: 'muted' };
  if (s === 'expired') return { label: 'Expired', tone: 'muted' };
  return { label: status.replace(/_/g, ' '), tone: 'muted' };
}

/** Live minutes-remaining for an offer inside its 10-minute undo window. */
function useCountdown(iso: string | undefined): number | null {
  const target = iso ? new Date(iso).getTime() : null;
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setTick((v) => v + 1), 1_000);
    return () => clearInterval(id);
  }, [target]);
  void tick;
  if (!target) return null;
  const ms = target - Date.now();
  return ms > 0 ? Math.ceil(ms / 60_000) : 0;
}

function UndoCountdownChip({ iso }: { iso: string }) {
  const mins = useCountdown(iso);
  if (mins == null || mins <= 0) return null;
  return (
    <span className="chip chip-warning" style={{ fontSize: 11 }}>
      Undo open · {mins}m left
    </span>
  );
}

export function StudentOffersPage() {
  const [seg, setSeg] = useState<Seg>('awaiting');
  const [q, setQ] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('deadlineAt');
  const [page, setPage] = useState(1);

  const listQuery = useOffers({
    page,
    limit: PAGE_SIZE,
    q: q.trim() || undefined,
    sortBy,
    sortDir: sortBy === 'ctc.amountInr' ? 'desc' : 'asc',
  });
  const offers = listQuery.data?.offers ?? [];
  const summary = listQuery.data?.summary?.byStatus ?? {};

  const countFor = (s: Seg): number => SEG_STATUSES[s].reduce((n, k) => n + (summary[k] ?? 0), 0);
  const awaitingCount = countFor('awaiting');
  const acceptedCount = countFor('accepted');
  const closedCount = countFor('closed');
  const totalCount = awaitingCount + acceptedCount + closedCount;

  const highestCTC = useMemo(() => {
    const accepted = offers.filter((o) => SEG_STATUSES.accepted.includes(o.status));
    if (accepted.length === 0) return null;
    return Math.max(...accepted.map((o) => o.ctc?.amountInr ?? 0));
  }, [offers]);

  const rowsForSeg = useMemo(
    () => offers.filter((o) => SEG_STATUSES[seg].includes(o.status)),
    [offers, seg],
  );

  return (
    <div className="grid gap-6">
      <header className="grid gap-1">
        <h1 className="h-page m-0">Offers</h1>
        <p className="helper m-0" style={{ maxWidth: '58ch' }}>
          {totalCount === 0
            ? "You haven't received any offers yet. Keep applying — offers land here as soon as your placement office signs off."
            : `${totalCount} offer${totalCount === 1 ? '' : 's'} · ${acceptedCount} accepted${
                highestCTC ? ` · highest ${formatLPA(highestCTC)}` : ''
              }`}
        </p>
      </header>

      {/* Segments + filters row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="segmented">
          <button aria-pressed={seg === 'awaiting'} onClick={() => { setSeg('awaiting'); setPage(1); }}>
            Awaiting <span className="helper ml-1">{awaitingCount}</span>
          </button>
          <button aria-pressed={seg === 'accepted'} onClick={() => { setSeg('accepted'); setPage(1); }}>
            Accepted <span className="helper ml-1">{acceptedCount}</span>
          </button>
          <button aria-pressed={seg === 'closed'} onClick={() => { setSeg('closed'); setPage(1); }}>
            Closed <span className="helper ml-1">{closedCount}</span>
          </button>
        </div>
        <input
          type="search"
          className="input"
          placeholder="Search company or role"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          style={{ minWidth: 220 }}
        />
        <select
          className="select"
          value={sortBy}
          onChange={(e) => { setSortBy(e.target.value as SortKey); setPage(1); }}
        >
          <option value="deadlineAt">Soonest deadline</option>
          <option value="ctc.amountInr">Highest CTC</option>
          <option value="createdAt">Most recent</option>
          <option value="acceptedAt">Recently accepted</option>
          <option value="releasedAt">Recently released</option>
        </select>
      </div>

      <AsyncBoundary
        kind="cards"
        loading={listQuery.isLoading}
        error={listQuery.isError ? (listQuery.error instanceof Error ? listQuery.error.message : true) : undefined}
        onRetry={() => { void listQuery.refetch(); }}
        empty={rowsForSeg.length === 0}
        emptyTitle={seg === 'awaiting' ? 'No offers awaiting your reply' : seg === 'accepted' ? 'No accepted offers yet' : 'Nothing closed'}
        emptyHint={seg === 'awaiting' ? 'When a company sends you an offer, it shows up here. In the meantime, keep applying.' : undefined}
        emptyAction={seg === 'awaiting' ? <Link to="/explore"><Button variant="primary">Browse opportunities</Button></Link> : undefined}
      >
        <ul className="grid gap-3" style={{ padding: 0, listStyle: 'none', margin: 0 }}>
          {rowsForSeg.map((o: OfferListItem) => {
            const st = statusChip(o.status);
            const deadline = classifyDeadline(o.deadlineAt ?? o.deadline);
            const showUndo = o.status === 'accepted' && o.undoWindowEndsAt && new Date(o.undoWindowEndsAt).getTime() > Date.now();
            return (
              <li key={o._id}>
                <Link
                  to={`/offers/${o._id}`}
                  className="card p-4 flex items-center gap-4"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <span
                    className="logo-tile"
                    style={{
                      width: 40, height: 40,
                      background: 'rgb(var(--bg-inkwell))',
                      color: 'rgb(var(--ink-inverse))',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 600, fontSize: '0.75rem',
                      borderRadius: 6,
                      flexShrink: 0,
                    }}
                  >
                    {initials(o.partner?.name ?? o.drive?.driveName)}
                  </span>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span className="truncate" style={{ display: 'block', fontWeight: 600 }}>
                      {o.partner?.name ?? 'Company'} — {o.role ?? 'Role'}
                    </span>
                    <span className="truncate helper" style={{ display: 'block', marginTop: 2 }}>
                      {o.ctc?.amountInr ? formatLPA(o.ctc.amountInr) : 'CTC on the letter'}
                      {o.drive?.driveName ? ` · ${o.drive.driveName}` : ''}
                    </span>
                    {/* Checks summary — small chip cluster only when data present */}
                    {typeof o.allChecksPassed === 'boolean' && seg !== 'closed' && (
                      <span className="helper" style={{ display: 'block', marginTop: 4, fontSize: 11 }}>
                        {o.allChecksPassed ? '✓ Checks passed' : '⚠ Some checks pending'}
                      </span>
                    )}
                  </span>
                  <span style={{ display: 'grid', gap: 4, justifyItems: 'end', flexShrink: 0 }}>
                    <span className={`chip chip-${st.tone === 'info' ? 'info' : st.tone === 'success' ? 'success' : st.tone === 'warning' ? 'warning' : 'muted'}`}>
                      {st.label}
                    </span>
                    {seg === 'awaiting' && deadline.days !== null && (
                      <span className={`chip chip-${deadline.tone}`} style={{ fontSize: 11 }}>
                        {deadline.label}
                      </span>
                    )}
                    {showUndo && <UndoCountdownChip iso={o.undoWindowEndsAt!} />}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </AsyncBoundary>

      {/* Pagination footer */}
      {(totalCount > PAGE_SIZE) && (
        <div className="flex items-center justify-between gap-2">
          <span className="helper">Page {page}</span>
          <div className="flex gap-2">
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </button>
            <button
              className="btn btn-ghost btn-sm"
              disabled={offers.length < PAGE_SIZE}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentOffersPage;
